import { useCallback, useEffect, useState } from "react";
import { useSessionStorage } from "usehooks-ts";
import { ExploreContext } from "../useExploreContext";
import { serializedContext } from "./serialization";

type StateSnapshot = {
  id: string;
  serialized: string;
};

export function useStateHistory(
  orgId: string,
  {
    params,
    doc,
    view,
    tabs,
  }: Pick<ExploreContext, "params" | "doc" | "view" | "tabs">
) {
  const [history, setHistory] = useSessionStorage<StateSnapshot[]>(
    `explore-state-history-24.01.05-${orgId}`,
    []
  );
  const [localSync, setLocalSync] = useState(history);
  const needsPushed = doc.id !== history.at(0)?.id;
  const serialized = serializedContext({ params, doc, view, tabs });
  useEffect(() => {
    if (needsPushed)
      setHistory((h) => {
        const first = h.at(0);
        if (first?.id === doc.id) return h; // redundant?

        const next = [
          { id: doc.id, serialized },
          ...h.filter(({ id }) => id !== doc.id).slice(0, 12),
        ];
        setLocalSync(next);
        return next;
      });
  }, [doc.id, needsPushed, serialized, setHistory]);

  const canUpdate = history.length > 0;
  const needsUpdate =
    canUpdate && !needsPushed && history[0].serialized !== serialized;
  useEffect(() => {
    if (needsUpdate)
      setHistory((h) => {
        const next = [
          {
            ...h.at(0)!,
            serialized,
          },
          ...h.slice(1),
        ];
        setLocalSync(next);
        return next;
      });
  }, [serialized, setHistory, needsUpdate]);
  const clear = useCallback(() => {
    setHistory([]);
    setLocalSync([]);
  }, [setHistory]);
  return { items: localSync, clear };
}
